import { useContext } from 'react'

import { useAuthorizedMe } from '@/common/api/modules/auth'
import {
  usePermissionTemplateDeleteApi,
  usePermissionTemplateListApi
} from '@/common/api/modules/permissionTemplate'
import { permissionTemplateEditPath, permissionTemplateNewPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'
import { useShowDeletedToast } from '@/common/app/toast'
import { PermissionTemplate } from '@/common/model/auth'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import Table from '@/components/view/atoms/table/Table'
import TableHeadItem from '@/components/view/atoms/table/TableHeadItem'
import TableRow from '@/components/view/atoms/table/TableRow'
import TableRowItem from '@/components/view/atoms/table/TableRowItem'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import { DeleteDialogContext } from '@/components/container/modal/dialog/delete/DeleteDialogProvider'

const PermissionTemplateListPage = () => {
  const siteId = useSiteId()
  const { canManage } = useAuthorizedMe()
  const showDeleted = useShowDeletedToast()
  const { showModal, hide } = useContext(DeleteDialogContext)
  const { data, isLoading, mutate } = usePermissionTemplateListApi({ site_id: siteId })
  const { trigger: deleteTemplate } = usePermissionTemplateDeleteApi({
    onSuccess: () => {
      hide()
      mutate()
    }
  })

  const onClickDelete = (template: PermissionTemplate) => {
    showModal({
      name: template.name,
      onClickDelete: () =>
        deleteTemplate({ site_id: siteId, template_id: template.id }).then(() =>
          showDeleted(template.name)
        )
    })
  }

  const templates = data ?? []

  return (
    <Main type={MainLayoutType.SMALL} bottomSpace>
      <PageTitle
        title={'権限管理'}
        description='サイト設定 — オペレーターに割り当てる権限テンプレートを管理します'
        rightNode={
          canManage ? (
            <Button
              link={permissionTemplateNewPath(siteId)}
              size='large'
              color={ButtonColor.PRIMARY_BRAND}
            >
              新規テンプレートを追加する
            </Button>
          ) : undefined
        }
      />
      <Content>
        {isLoading ? (
          <PageListLoading />
        ) : (
          <Area tableWrap noBorder>
            <Table>
              <thead>
                <TableRow head>
                  {['テンプレート名', '種別', '記事承認', ...(canManage ? ['操作'] : [])].map(
                    (label) => (
                      <TableHeadItem key={label}>
                        <Text>{label}</Text>
                      </TableHeadItem>
                    )
                  )}
                </TableRow>
              </thead>
              <tbody>
                {templates.length === 0 ? (
                  <TableRow>
                    <TableRowItem>
                      <Text color={TextColor.SECONDARY}>権限テンプレートがありません</Text>
                    </TableRowItem>
                  </TableRow>
                ) : (
                  templates.map((t) => (
                    <TableRow key={t.id}>
                      <TableRowItem>
                        <Text>{t.name}</Text>
                      </TableRowItem>
                      <TableRowItem>
                        {t.is_system ? (
                          <Badge badgeType={BadgeType.SECONDARY}>システム標準</Badge>
                        ) : (
                          <Text color={TextColor.SECONDARY}>カスタム</Text>
                        )}
                      </TableRowItem>
                      <TableRowItem>
                        {t.can_approve_article ? (
                          <Badge badgeType={BadgeType.SQUARE_PRIMARY}>承認役割あり</Badge>
                        ) : (
                          <Badge badgeType={BadgeType.SQUARE_SECONDARY}>なし</Badge>
                        )}
                      </TableRowItem>
                      {canManage && (
                        <TableRowItem>
                          <ItemAlign gapX={8} align='center'>
                            <Button
                              link={permissionTemplateEditPath(siteId, t.id)}
                              color={ButtonColor.SECONDARY}
                            >
                              編集する
                            </Button>
                            {!t.is_system && (
                              <Button
                                color={ButtonColor.SECONDARY}
                                square
                                onClick={() => onClickDelete(t)}
                              >
                                <Icon type={IconType.DELETE} color={IconColor.CRITICAL} line />
                              </Button>
                            )}
                          </ItemAlign>
                        </TableRowItem>
                      )}
                    </TableRow>
                  ))
                )}
              </tbody>
            </Table>
          </Area>
        )}
      </Content>
    </Main>
  )
}

export default PermissionTemplateListPage
